import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  MenuItem,
  Typography,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import AddBoxIcon from "@mui/icons-material/AddBox";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import {
  GetInventoryLocations,
  GetInventoryStock,
} from "../../../services/InventoryTrackerService";
import { PrintInventoryLabel } from "../../../../services/barcodeService";
import CommonLoader from "../../common/CommonLoader";
import InventoryScanDialog from "./InventoryScanDialog";
import StockActionDialog, { formatDate } from "./StockActionDialog";
import useSortedPaging from "./useSortedPaging";

/**
 * ENB-584 Inventory Tracker: scan a product or location, see stock by location or product
 * (per lot / expiry), and receive, use, move or count it.
 */
const InventoryTracker = () => {
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [product, setProduct] = useState(null);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);

  useEffect(() => {
    loadLocations();
    fetchStock("", null, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLocations = async () => {
    try {
      const response = await GetInventoryLocations();
      setLocations(response?.results || []);
    } catch {
      NotificationManager.error("Error loading locations");
    }
  };

  const fetchStock = async (location = locationId, selectedProduct = product, search = searchText) => {
    try {
      setLoading(true);
      const response = await GetInventoryStock(location, selectedProduct?.productId ?? "", search);
      const rows = response?.results || [];
      // A scanned variant label narrows the list to that variant.
      setStock(
        selectedProduct?.productVariantId
          ? rows.filter((r) => r.productVariantId === selectedProduct.productVariantId)
          : rows
      );
    } catch {
      NotificationManager.error("Error loading stock");
    } finally {
      setLoading(false);
    }
  };

  const handleFound = (result) => {
    setScanOpen(false);
    if (result.matchType === "Location") {
      setProduct(null);
      setSearchText("");
      setLocationId(result.location.id);
      fetchStock(result.location.id, null, "");
    } else {
      setProduct(result);
      setSearchText("");
      setLocationId("");
      fetchStock("", result, "");
    }
  };

  const handleAddProduct = (code) => {
    setScanOpen(false);
    navigate("/vlp/inventory-products/add", { state: { sku: code } });
  };

  const clearProduct = () => {
    setProduct(null);
    fetchStock(locationId, null, searchText);
  };

  const handlePrint = async (row) => {
    try {
      await PrintInventoryLabel("inventoryLabel", {
        "{{name}}": row.variantName ? `${row.productName} - ${row.variantName}` : row.productName,
        "{{barcode}}": row.itemNumber || "",
        "{{expiration}}": row.expirationDate ? formatDate(row.expirationDate) : "-",
        "{{quantity}}": row.stockLevel,
        "{{lot}}": row.lotNumber || "-",
      });
      NotificationManager.success("Label printed");
    } catch (error) {
      console.error("Error printing label:", error);
      NotificationManager.error(error?.message || "Error printing label");
    }
  };

  const openAction = (action, row = null) => setActionDialog({ action, row });

  const handleActionDone = () => {
    setActionDialog(null);
    fetchStock();
  };

  const columns = [
    { label: "Product", field: "productName" },
    { label: "Item #", field: "itemNumber" },
    { label: "Variant", field: "variantName" },
    { label: "Location", field: "locationName" },
    { label: "Lot", field: "lotNumber" },
    { label: "Expiration", field: "expirationDate" },
    { label: "On Hand", field: "stockLevel" },
    { label: "Actions" },
  ];

  const { header, pageRows, pagination } = useSortedPaging(stock, columns);

  const totalOnHand = stock.reduce((sum, row) => sum + (Number(row.stockLevel) || 0), 0);

  // Expired once the expiry day has passed (calendar dates compared as text, no time zone shift).
  const today = new Date().toLocaleDateString("en-CA");
  const isExpired = (row) => row.expirationDate && String(row.expirationDate).split("T")[0] < today;

  return (
    <div className="vlp-component-container">
      {/* Scan sits on the title row, like New on the other inventory screens. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginRight: "20px",
        }}
      >
        <span className="title">Inventory Tracker </span>
        <Button variant="contained" color="primary" startIcon={<QrCodeScannerIcon />} onClick={() => setScanOpen(true)}>
          Scan
        </Button>
      </div>
      <hr />

      {/* A form, like Supply Orders, so Enter in the search field runs the search. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          fetchStock();
        }}
        style={{ display: "flex", marginBottom: "16px", alignItems: "center", gap: "12px" }}
      >
        <Tooltip title="Search by product, item number, variant, lot or location. Press Enter to search" arrow placement="top">
          <TextField
            label="Search product, item #, lot"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: "20%" }}
          />
        </Tooltip>
        <Tooltip title="Shows stock at this location and everything inside it" arrow placement="top">
          <TextField
            select
            label="Location"
            variant="outlined"
            size="small"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            style={{ width: "20%" }}
          >
            <MenuItem value="">All</MenuItem>
            {locations.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.fullPath}
              </MenuItem>
            ))}
          </TextField>
        </Tooltip>
        <Button variant="contained" color="primary" type="submit">
          Search
        </Button>
      </form>

      {product && (
        <Paper elevation={2} sx={{ p: 2, mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <div style={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">
              {product.productName}
              {product.variantName ? ` - ${product.variantName}` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Item #: {product.itemNumber || "-"} | On hand: {totalOnHand}
            </Typography>
          </div>
          <Button variant="contained" color="success" startIcon={<AddBoxIcon />} onClick={() => openAction("Receive")}>
            Receive
          </Button>
          <IconButton onClick={clearProduct} title="Clear product">
            <CloseIcon />
          </IconButton>
        </Paper>
      )}

      <Paper style={{ marginRight: "20px" }}>
        <TableContainer>
          <Table sx={{ minWidth: 400 }} size="small">
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.primary.main }}>
              {header}
            </TableHead>
            <TableBody>
              {pageRows.map((row, index) => (
                <TableRow
                  key={row.id}
                  sx={{ backgroundColor: index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white" }}
                >
                  <TableCell>{row.productName}</TableCell>
                  <TableCell>{row.itemNumber}</TableCell>
                  <TableCell>{row.variantName}</TableCell>
                  <TableCell>{row.locationName}</TableCell>
                  <TableCell>{row.lotNumber}</TableCell>
                  <TableCell>
                    {formatDate(row.expirationDate)}
                    {isExpired(row) && <Chip label="Expired" size="small" color="error" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{row.stockLevel}</TableCell>
                  <TableCell>
                    <div style={{ minWidth: "150px" }}>
                      <Tooltip title="Use">
                        <IconButton size="small" color="error" onClick={() => openAction("Use", row)}>
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move">
                        <IconButton size="small" color="primary" onClick={() => openAction("Move", row)}>
                          <DriveFileMoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Count">
                        <IconButton size="small" style={{ color: "orange" }} onClick={() => openAction("Count", row)}>
                          <FactCheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Print label">
                        <IconButton size="small" onClick={() => handlePrint(row)}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && stock.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    No stock found.{product ? " Use Receive to add some." : ""}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination}
      </Paper>

      <InventoryScanDialog
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onFound={handleFound}
        onAddProduct={handleAddProduct}
      />

      {actionDialog && (
        <StockActionDialog
          open={Boolean(actionDialog)}
          action={actionDialog.action}
          product={actionDialog.row ? null : product}
          stockRow={actionDialog.row}
          onClose={() => setActionDialog(null)}
          onDone={handleActionDone}
        />
      )}

      <CommonLoader loading={loading} />
    </div>
  );
};

export default InventoryTracker;
